/**
 * Wake Lock Utility
 * Prevents system sleep during long-running analysis tasks
 */

// Type declarations for Wake Lock API
declare global {
  interface Navigator {
    wakeLock?: WakeLock;
  }
}

interface WakeLock {
  request(type: 'screen'): Promise<WakeLockSentinel>;
}

interface WakeLockSentinel extends EventTarget {
  readonly released: boolean;
  readonly type: 'screen';
  release(): Promise<void>;
}

class WakeLockManager {
  private wakeLock: WakeLockSentinel | null = null;
  private isSupported: boolean = false;

  constructor() {
    this.isSupported = 'wakeLock' in navigator && navigator.wakeLock !== undefined;
    if (!this.isSupported) {
      }
  }

  /**
   * Request wake lock to prevent system sleep
   */
  async requestWakeLock(reason: string = 'Analysis running'): Promise<boolean> {
    if (!this.isSupported) {
      return false;
    }

    try {
      // Release existing wake lock if any
      await this.releaseWakeLock();

      // Request new wake lock
      this.wakeLock = await navigator.wakeLock!.request('screen');
      
      // Handle wake lock release events
      this.wakeLock.addEventListener('release', () => {
        this.wakeLock = null;
      });

      return true;
    } catch (error) {
      console.error('❌ Failed to acquire wake lock:', error);
      return false;
    }
  }

  /**
   * Release wake lock to allow system sleep
   */
  async releaseWakeLock(): Promise<void> {
    if (this.wakeLock) {
      try {
        await this.wakeLock.release();
        } catch (error) {
        console.error('❌ Failed to release wake lock:', error);
      }
      this.wakeLock = null;
    }
  }

  /**
   * Check if wake lock is currently active
   */
  isActive(): boolean {
    return this.wakeLock !== null && !this.wakeLock.released;
  }

  /**
   * Get wake lock status information
   */
  getStatus() {
    return {
      supported: this.isSupported,
      active: this.isActive(),
      type: this.wakeLock?.type || null
    };
  }
}

// Export singleton instance
export const wakeLockManager = new WakeLockManager();

// Helper functions for easy usage
export const preventSleep = (reason?: string) => wakeLockManager.requestWakeLock(reason);
export const allowSleep = () => wakeLockManager.releaseWakeLock();
export const isWakeLockActive = () => wakeLockManager.isActive();